/**
 * React Query hooks for community-level asset operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAssets, createAsset, updateAsset, deleteAsset } from '../api/assets';
import { communityKeys } from './useCommunities';

/** Query key factory for community assets. */
export const assetKeys = {
  all: (communityId) => ['communityAssets', communityId],
};

/**
 * List all community-level assets.
 * @param {string|null} communityId - UUID
 */
export function useAssets(communityId) {
  return useQuery({
    queryKey: assetKeys.all(communityId),
    queryFn: () => getAssets(communityId),
    enabled: !!communityId,
  });
}

/**
 * Create a community-level asset.
 * @param {string} communityId - UUID
 */
export function useCreateAsset(communityId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createAsset(communityId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.all(communityId) });
      queryClient.invalidateQueries({ queryKey: communityKeys.detail(communityId) });
    },
  });
}

/**
 * Update a community-level asset.
 * @param {string} communityId - UUID
 */
export function useUpdateAsset(communityId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, data }) => updateAsset(communityId, assetId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.all(communityId) });
      queryClient.invalidateQueries({ queryKey: communityKeys.detail(communityId) });
    },
  });
}

/**
 * Delete a community-level asset.
 * @param {string} communityId - UUID
 */
export function useDeleteAsset(communityId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assetId) => deleteAsset(communityId, assetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.all(communityId) });
      queryClient.invalidateQueries({ queryKey: communityKeys.detail(communityId) });
    },
  });
}
